import React, { useState, useEffect } from 'react';
import { Dialog } from 'primereact/dialog';
import { MultiSelect } from 'primereact/multiselect';
import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import { postRequest, getRequest } from '../services/apiService';
import { toast } from 'react-toastify';

const UploadTaskForm = ({ isOpen, onClose }) => {
  const [activeTab, setActiveTab] = useState('Hull Outfit');
  const [selectedBlocks, setSelectedBlocks] = useState({
    'Hull Structure': [],
    'Pipping': [],
    'Equipment': [],
    'Trunking Ducting': []
  });
  const [selectedProject, setSelectedProject] = useState(null);
  const [selectedShip, setSelectedShip] = useState(null);
  const [selectedHullOutfit, setSelectedHullOutfit] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [projects, setProjects] = useState([]);
  const [ships, setShips] = useState([]);
  const [hullOutfitOptions, setHullOutfitOptions] = useState([]);
  const [dynamicData, setDynamicData] = useState({});
  const [isExporting, setIsExporting] = useState(false);

  // Reset form when opened
  useEffect(() => {
    if (isOpen) {
      setActiveTab('Hull Outfit');
      setSelectedProject(null);
      setSelectedShip(null);
      setSelectedHullOutfit(null);
      setSelectedBlocks({
        'Hull Structure': [],
        'Pipping': [],
        'Equipment': [],
        'Trunking Ducting': []
      });
      setIsSubmitting(false);
      setShips([]);
      setHullOutfitOptions([]);
      setDynamicData({});
      fetchProjects();
    }
  }, [isOpen]);

  // Fetch data based on active tab
  useEffect(() => {
    if (isOpen && activeTab !== 'Hull Outfit') {
      fetchTabData();
    }
  }, [isOpen, activeTab]);

  // Fetch projects on component mount
  useEffect(() => {
    if (isOpen) {
      fetchProjects();
    }
  }, [isOpen]);

  // Fetch ships when project is selected
  useEffect(() => {
    if (selectedProject) {
      fetchShips(selectedProject.id);
    } else {
      setShips([]);
      setSelectedShip(null);
    }
  }, [selectedProject]);

  // Fetch hull outfit options when ship is selected in Hull Outfit tab
  useEffect(() => {
    if (activeTab === 'Hull Outfit' && selectedShip) {
      fetchHullOutfitOptions();
    }
  }, [activeTab, selectedShip]);

  const fetchTabData = async () => {
    try {
      let endpoint;
      let params = {};

      switch (activeTab) {
        case 'Hull Structure':
          endpoint = 'master/mpr-block-master/';
          if (selectedShip) {
            params = { ship_id: selectedShip };
          }
          break;
        case 'Pipping':
          endpoint = 'master/systems/';
          if (selectedShip) {
            params = { ship_id: selectedShip };
          }
          break;
        case 'Equipment':
          endpoint = 'master/equipment/';
          if (selectedShip) {
            params = { ship_id: selectedShip };
          }
          break;
        case 'Trunking Ducting':
          endpoint = 'master/mpr-block-master/';
          if (selectedShip) {
            params = { ship_id: selectedShip };
          }
          break;
        default:
          return;
      }

      const response = await getRequest(endpoint, params);
      let dataList = [];

      if (activeTab === 'Equipment' || activeTab === 'Pipping') {
        if (response?.data) {
          dataList = response.data;
        }
      } else {
        if (response?.results?.data) {
          dataList = response.results.data;
        }
      }

      if (Array.isArray(response)) {
        dataList = response;
      }

      const transformedData = dataList.map(item => {
        switch (activeTab) {
          case 'Hull Structure':
            return {
              id: item.id,
              name: item.name || item.block_name,
              description: item.description || item.block_description
            };
          case 'Pipping':
            return {
              id: item.id,
              name: item.name,
              description: item.description || ''
            };
          case 'Equipment':
            return {
              id: item.id,
              name: item.name || item.equipment_name,
              description: item.description || item.equipment_description
            };
          case 'Trunking Ducting':
            return {
              id: item.id,
              name: item.name || item.unit_name,
              description: item.description || item.unit_description
            };
          default:
            return item;
        }
      });

      setDynamicData(prev => ({
        ...prev,
        [activeTab]: transformedData
      }));
    } catch (error) {
      console.error(`Error fetching ${activeTab} data:`, error);
      toast.error(`Failed to fetch ${activeTab} data`);
    }
  };

  const fetchHullOutfitOptions = async () => {
    try {
      const response = await getRequest('master/hull-outfit/');
      let options = [];

      if (response?.data) {
        options = response.data;
      } else if (Array.isArray(response)) {
        options = response;
      }

      setHullOutfitOptions(options);
    } catch (error) {
      toast.error('Failed to fetch hull outfit options.');
      console.error('Error fetching hull outfit options:', error);
    }
  };

  const fetchProjects = async () => {
    try {
      const response = await getRequest('master/projects/');
      let projectList = [];

      if (response?.data) {
        projectList = response.data;
      } else if (Array.isArray(response)) {
        projectList = response;
      }

      const parentProjects = projectList.filter(project => !project.parent);
      setProjects(parentProjects);
    } catch (error) {
      toast.error('Failed to fetch projects.');
      console.error('Error fetching projects:', error);
    }
  };

  const fetchShips = async (projectId) => {
    try {
      const response = await getRequest(`master/projects/?parent_id=${projectId}`);
      let shipsList = [];

      if (response?.data) {
        shipsList = response.data;
      } else if (Array.isArray(response)) {
        shipsList = response;
      }

      setShips(shipsList);
    } catch (error) {
      toast.error('Failed to fetch ships.');
      console.error('Error fetching ships:', error);
    }
  };

  const handleProjectChange = (e) => {
    const selectedProject = e.value;
    setSelectedProject(selectedProject);
    setSelectedShip(null);
    setSelectedHullOutfit(null);

    if (selectedProject) {
      fetchShips(selectedProject);
    } else {
      setShips([]);
    }
  };

  const handleClose = () => {
    toast.info('Form closed');
    onClose();
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (!selectedShip) {
      toast.error('Please select a ship');
      return;
    }

    try {
      setIsSubmitting(true);
      const formData = new FormData();
      formData.append('file', file);

      let endpoint;

      switch (activeTab) {
        case 'Hull Outfit':
          endpoint = `/transaction/import-pert-data/${selectedShip}/`;
          // if (selectedHullOutfit) {
          //   formData.append('hull_outfit_id', selectedHullOutfit);
          // }
          break;
        case 'Hull Structure':
          endpoint = `/transaction/import-hull-structure/${selectedShip}/`;
          if (selectedBlocks[activeTab].length > 0) {
            formData.append('block_ids', JSON.stringify(selectedBlocks[activeTab]));
          }
          break;
        case 'Pipping':
          endpoint = `/transaction/import-pipping/${selectedShip}/`;
          if (selectedBlocks[activeTab].length > 0) {
            formData.append('system_ids', JSON.stringify(selectedBlocks[activeTab]));
          }
          break;
        case 'Equipment':
          endpoint = `/transaction/import-equipment/${selectedShip}/`;
          if (selectedBlocks[activeTab].length > 0) {
            formData.append('equipment_ids', JSON.stringify(selectedBlocks[activeTab]));
          }
          break;
        case 'Trunking Ducting':
          endpoint = `/transaction/import-trunking-ducting/${selectedShip}/`;
          if (selectedBlocks[activeTab].length > 0) {
            formData.append('block_ids', JSON.stringify(selectedBlocks[activeTab]));
          }
          break;
        default:
          toast.error('Import not configured for this tab yet.');
          setIsSubmitting(false);
          return;
      }

      const response = await postRequest(endpoint, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      const data = await response.data;

      if (data.error_file_path) {
        const link = document.createElement('a');
        link.href = `${process.env.REACT_APP_API_BASE_URL}${data.error_file_path}`;
        link.download = data.error_file_path.split('/').pop();
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }

      if (data.status === 'success') {
        toast.success(data.message || 'Import successful');
        onClose();
      } else if (data.status === 'partial_success') {
        toast.warning(data.message || 'Import completed with some errors');
        onClose();
      } else {
        toast.error(data.message || 'Import failed');
      }

    } catch (error) {
      console.error('Error uploading file:', error);
      toast.error(error.message || 'Error uploading file');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleExportCSV = async () => {
    if (!selectedShip) {
      toast.error('Please select a ship for export.');
      return;
    }

    try {
      setIsExporting(true);
      let endpoint;
      let payload = {};
      const fileName = `${activeTab.toLowerCase().replace(/\s+/g, '_')}_template.csv`;

      switch (activeTab) {
        case 'Hull Outfit':
          endpoint = 'master/hull-outfit-sample-download/';
          // if (!selectedHullOutfit) {
          //   toast.error('Hull Outfit selection is required for this export.');
          //   setIsExporting(false);
          //   return;
          // }
          // payload = { hull_outfit_id: selectedHullOutfit };
          break;

        case 'Hull Structure':
          endpoint = 'master/hull-structure-sample-download/';
          if (selectedBlocks[activeTab]?.length > 0) {
            payload = { block_ids: selectedBlocks[activeTab] };
          } else {
            toast.warn('No blocks selected for export.');
            setIsExporting(false);
            return;
          }
          break;

        case 'Pipping':
          endpoint = 'master/piping-sample-download/';
          if (selectedBlocks[activeTab]?.length > 0) {
            payload = { system_ids: selectedBlocks[activeTab] };
          } else {
            toast.warn('No systems selected for export.');
            setIsExporting(false);
            return;
          }
          break;

        case 'Equipment':
          endpoint = 'master/equipment-sample-download/';
          if (selectedBlocks[activeTab]?.length > 0) {
            payload = { equipment_ids: selectedBlocks[activeTab] };
          } else {
            toast.warn('No equipment selected for export.');
            setIsExporting(false);
            return;
          }
          break;

        case 'Trunking Ducting':
          endpoint = 'master/trunking-ducting-sample-download/';
          if (selectedBlocks[activeTab]?.length > 0) {
            payload = { block_ids: selectedBlocks[activeTab] };
          } else {
            toast.warn('No blocks selected for export.');
            setIsExporting(false);
            return;
          }
          break;

        default:
          toast.error('Export not configured for this tab yet.');
          setIsExporting(false);
          return;
      }

      const response = await postRequest(endpoint, payload);

      // Direct file URL from the API response
      const fileUrl = response.data.file_url;
      console.log('File download URL:', fileUrl);

      if (fileUrl) {
        const link = document.createElement('a');
        link.href = fileUrl;
        link.download = fileUrl.split('/').pop(); // Optional, sets the filename from the URL
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }


      toast.success('CSV template downloaded successfully');
      onClose();
    } catch (error) {
      console.error('Error exporting CSV:', error);
      if (error.response?.data) {
        try {
          const text = await new Response(error.response.data).text();
          const data = JSON.parse(text);
          if (data.error_file_path) {
            const errorUrl = `${process.env.REACT_APP_API_BASE_URL}${data.error_file_path}`;
            window.open(errorUrl, '_blank');
          }
          toast.error(data.message || 'Export failed with errors');
        } catch {
          toast.error('Failed to export CSV template and parse error response');
        }
      } else {
        toast.error(error.message || 'Failed to export CSV template');
      }
    } finally {
      setIsExporting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-10 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 transition-opacity" aria-hidden="true">
          <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
        </div>

        <div className="inline-block align-middle bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full">
          <div className="bg-[#1e3a8a] text-white px-6 py-4 rounded-t-lg flex justify-between items-center">
            <h3 className="text-lg leading-6 font-medium">Upload Task Information</h3>
            <button
              onClick={handleClose}
              className="text-white hover:text-gray-200"
              disabled={isSubmitting}
            >
              <span className="sr-only">Close</span>
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
              </svg>
            </button>
          </div>
          <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4 ">
            {/* Tabs */}
            <div className="border-b border-gray-200">
              <nav className="-mb-px flex">
                {['Hull Outfit', 'Hull Structure', 'Pipping', 'Equipment', 'Trunking Ducting'].map((tab) => (
                  <button
                    key={tab}
                    onClick={() => setActiveTab(tab)}
                    className={`px-4 py-2 text-sm font-medium ${activeTab === tab
                      ? 'border-b-2 border-blue-600 text-blue-600'
                      : 'text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      }`}
                  >
                    {tab}
                  </button>
                ))}
              </nav>
            </div>

            {/* Tab Content */}
            <div className="mt-4">
              <div className="space-y-4">
                {/* Project and Ship Selection */}
                <div className="flex gap-4">
                  <div className="flex-1">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Project
                    </label>
                    <Dropdown
                      value={selectedProject}
                      options={projects}
                      onChange={handleProjectChange}
                      optionLabel="name"
                      optionValue="id"
                      placeholder="Select Project"
                      className="w-full rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      panelClassName="w-full max-w-[200px]"

                    />
                  </div>
                  <div className="flex-1">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Ship
                    </label>
                    <Dropdown
                      value={selectedShip}
                      options={ships}
                      onChange={(e) => setSelectedShip(e.value)}
                      optionLabel="name"
                      optionValue="id"
                      placeholder="Select Ship"
                      className="w-full border border-gray-300 rounded-md"
                      disabled={!selectedProject}
                    />
                  </div>
                </div>

                {/* Hull Outfit Selection */}
                {/* {activeTab === 'Hull Outfit' && (
                  <div className="mb-4">
                    {/* <label className="block text-sm font-medium text-gray-700 mb-2">
                      Select Hull Outfit
                    </label> */}
                {/* <Dropdown
                      value={selectedHullOutfit}
                      options={hullOutfitOptions}
                      onChange={(e) => setSelectedHullOutfit(e.value)}
                      optionLabel="name"
                      optionValue="id"
                      placeholder="Select Hull Outfit"
                      className="w-full border border-gray-300 rounded-md"
                      disabled={!selectedShip}
                    /> */}
                {/* </div> */}
                {/* )} */}

                {/* Block Selection for other tabs */}
                {activeTab !== 'Hull Outfit' && (
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Select {activeTab}
                    </label>
                    <div>
                      <MultiSelect
                        value={selectedBlocks[activeTab]}
                        options={dynamicData[activeTab] || []}
                        onChange={(e) => setSelectedBlocks(prev => ({
                          ...prev,
                          [activeTab]: e.value
                        }))}
                        optionLabel="name"
                        optionValue="id"
                        placeholder={`Select ${activeTab}`}
                         className="w-full rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              panelClassName="w-full max-w-[200px]"
                        display="chip"
                        showSelectAll={true}
                        filter
                        filterBy="name,description"
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* Upload and Export buttons */}
              <div className="flex justify-end space-x-4 mt-4 pt-4 border-t">
                <Button
                  label={isExporting ? 'Exporting...' : 'Export CSV'}
                  icon="pi pi-download"
                  className="p-button-info"
                  onClick={handleExportCSV}
                  disabled={
                    !selectedProject ||
                    !selectedShip ||
                    (activeTab !== 'Hull Outfit' && selectedBlocks[activeTab].length === 0) ||
                    isExporting
                  }
                />
                <div className="relative">
                  <input
                    type="file"
                    accept=".csv"
                    onChange={handleFileUpload}
                    className="hidden"
                    id="file-upload"
                    disabled={
                      !selectedProject ||
                      !selectedShip ||
                      (activeTab !== 'Hull Outfit' && selectedBlocks[activeTab].length === 0) ||
                      isSubmitting
                    }
                  />
                  <label
                    htmlFor="file-upload"
                    className={`p-button p-component bg-[#1e3a8a] text-white border-0 shadow hover:bg-[#1e3a8a]/90 active:scale-95 transition-all flex items-center gap-2 font-semibold px-6 py-2 rounded-lg ${
                      (!selectedProject ||
                        !selectedShip ||
                        (activeTab !== 'Hull Outfit' && selectedBlocks[activeTab].length === 0) ||
                        isSubmitting)
                        ? 'opacity-50 cursor-not-allowed'
                        : ''
                    }`}
                  >
                    <i className="pi pi-upload mr-2"></i>
                    {isSubmitting ? 'Uploading...' : 'Upload File'}
                  </label>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UploadTaskForm; 